import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { AdviseEngagement } from '../types/advise';
import {
  fetchAdviseEngagements as apiFetchAdviseEngagements,
  fetchAdviseEngagementById as apiFetchAdviseEngagementById,
  updateAdviseEngagement as apiUpdateAdviseEngagement,
  deleteAdviseEngagement as apiDeleteAdviseEngagement
} from '../Api/Advise/Engagements';
import { addError } from './Error';

export interface UseAdviseEngagementsReturn {
  engagements: AdviseEngagement[];
  activeEngagement: AdviseEngagement | null;
  fetchEngagements: (forceRefresh?: boolean) => Promise<void>;
  fetchEngagementById: (id: string) => Promise<void>;
  updateEngagement: (id: string, engagement: Partial<AdviseEngagement>) => Promise<AdviseEngagement | null>;
  deleteEngagement: (id: string) => Promise<boolean>;
  setActiveEngagement: (engagement: AdviseEngagement | null) => void;
  clear: () => void;
}

const defaultData: AdviseEngagement[] = [];
const subject = new BehaviorSubject<AdviseEngagement[]>(defaultData);
const activeSubject = new BehaviorSubject<AdviseEngagement | null>(null);
const loadingSubject = new BehaviorSubject<boolean>(false);
const emitErrorState = (message?: string): void => addError('advise-engagement', message);

export const getAdviseEngagementSubject = (): BehaviorSubject<AdviseEngagement[]> => subject;
export const getAdviseEngagements = (): AdviseEngagement[] => subject.value;
export const getAdviseActiveEngagementSubject = (): BehaviorSubject<AdviseEngagement | null> => activeSubject;
export const getAdviseActiveEngagement = (): AdviseEngagement | null => activeSubject.value;
export const getAdviseEngagementLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;

export const useAdviseEngagements = (): UseAdviseEngagementsReturn => {
  const [engagements, setEngagements] = useState(subject.getValue());
  const [active, setActive] = useState<AdviseEngagement | null>(activeSubject.getValue());

  useEffect(() => {
    const engagementSub = subject.subscribe(setEngagements);
    const activeSub = activeSubject.subscribe(setActive);
    return () => {
      engagementSub.unsubscribe();
      activeSub.unsubscribe();
    };
  }, []);

  const fetchEngagements = async (forceRefresh = false): Promise<void> => {
    if (loadingSubject.getValue()) return;
    if (subject.getValue().length > 0 && !forceRefresh) return;
    loadingSubject.next(true);

    const { data, error } = await apiFetchAdviseEngagements();
    if (data) {
      emitErrorState();
      subject.next(data);
      setEngagements(data);
    }
    if (error) {
      subject.next(defaultData);
      emitErrorState(error.message);
    }
    loadingSubject.next(false);
  };

  const fetchEngagementById = async (id: string): Promise<void> => {
    loadingSubject.next(true);
    const { data, error } = await apiFetchAdviseEngagementById(id);
    if (data) {
      emitErrorState();
      activeSubject.next(data);
      setActive(data);
    }
    if (error) {
      emitErrorState(error.message);
    }
    loadingSubject.next(false);
  };

  const updateEngagement = async (
    id: string,
    engagement: Partial<AdviseEngagement>
  ): Promise<AdviseEngagement | null> => {
    const { data, error } = await apiUpdateAdviseEngagement(id, engagement);
    if (data) {
      emitErrorState();
      const updated = subject.getValue().map((c) => (c.id === id ? data : c));
      subject.next(updated);
      setEngagements(updated);
      if (active?.id === id) {
        activeSubject.next(data);
        setActive(data);
      }
      return data;
    }
    if (error) emitErrorState(error.message);
    return null;
  };

  const deleteEngagement = async (id: string): Promise<boolean> => {
    const { error } = await apiDeleteAdviseEngagement(id);
    if (!error) {
      emitErrorState();
      const updated = subject.getValue().filter((c) => c.id !== id);
      subject.next(updated);
      setEngagements(updated);
      if (active?.id === id) {
        activeSubject.next(null);
        setActive(null);
      }
      return true;
    }
    emitErrorState(error.message);
    return false;
  };

  const setActiveEngagement = (engagement: AdviseEngagement | null) => {
    activeSubject.next(engagement);
    setActive(engagement);
  };

  const clear = () => {
    subject.next(defaultData);
    setEngagements(defaultData);
    activeSubject.next(null);
    setActive(null);
  };

  return {
    engagements,
    activeEngagement: active,
    fetchEngagements,
    fetchEngagementById,
    updateEngagement,
    deleteEngagement,
    setActiveEngagement,
    clear
  };
};
